// Import necessary modules and components
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider, // Divider is imported and used for list item separation
    IconButton,
    CircularProgress,
    Alert,
    Chip,
    Paper,
    Snackbar,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Drawer,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportIcon from '@mui/icons-material/BugReport';
import { StoreContext } from '../Hooks/store'; // Assuming StoreContext is correctly implemented
import useUserInteractionTracker from '../Hooks/useUserInteractionTracker'; // Assuming this hook exists
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // MUI Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CommentIcon from '@mui/icons-material/Comment';
import DeleteIcon from '@mui/icons-material/Delete'; // Added for delete functionality
// Ensure date-fns and the format function are correctly imported
import { format, parseISO } from 'date-fns'; // Used for formatting timestamps
import ErrorBoundary from '../../ErrorBoundary'; // Assuming ErrorBoundary component exists and is correctly implemented
// Assuming these API functions exist and getTodoItems is from Supabase, others (comments/time) might be from ClickHouse or other services
import { getTodoItems, getTotalTimeSpentOnTodo } from '../../api/todo';
import { useAuth } from '../Hooks/useAuth'; // Import useAuth hook to get current user details

// Define the Deno backend API base URL from environment variables
const DENOKV_API_URL = process.env.REACT_APP_DENOKV_API_URL;

// Add console log to check the value at runtime
//console.log('TodoComments: DENOKV_API_URL resolved to:', DENOKV_API_URL);


// Custom hook for console logging with history
const useConsoleLogger = () => {
    const [logs, setLogs] = useState([]);
    const maxLogs = 100; // Maximum number of logs to keep

    // Override console methods to capture logs
    useEffect(() => {
        const originalConsole = {
            log: console.log,
            error: console.error,
            warn: console.warn,
            info: console.info
        };

        // Create new console methods that capture logs
        console.log = (...args) => {
            originalConsole.log(...args);
            addLog('log', args);
        };

        console.error = (...args) => {
            originalConsole.error(...args);
            addLog('error', args);
        };

        console.warn = (...args) => {
            originalConsole.warn(...args);
            addLog('warn', args);
        };

        console.info = (...args) => {
            originalConsole.info(...args);
            addLog('info', args);
        };

        // Function to add a log to the history
        function addLog(type, args) {
            const timestamp = new Date().toISOString();
            const formattedArgs = args.map(arg => {
                if (typeof arg === 'object') {
                    try {
                        // Use JSON.stringify with a replacer to handle circular references if necessary
                        return JSON.stringify(arg, (key, value) => {
                            if (typeof value === 'object' && value !== null) {
                                if (value instanceof Error) {
                                    return { message: value.message, name: value.name, stack: value.stack };
                                }
                                // Could add more checks for specific object types if needed
                            }
                            return value;
                        }, 2); // Use indentation for readability
                    } catch (e) {
                         // Fallback for complex objects that might still fail stringify
                         if (arg instanceof Error) {
                              return `Error: ${arg.message}`;
                         }
                         return String(arg);
                    }
                }
                return String(arg);
            }).join(' ');

            setLogs(prevLogs => {
                const newLogs = [...prevLogs, { type, message: formattedArgs, timestamp }];
                // Keep only the last maxLogs entries
                return newLogs.slice(-maxLogs);
            });
        }

        // Restore original console methods on cleanup
        return () => {
            console.log = originalConsole.log;
            console.error = originalConsole.error;
            console.warn = originalConsole.warn;
            console.info = originalConsole.info;
        };
    }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

    return logs;
};

// Debug Console Component
const DebugConsole = ({ logs }) => {
    const [open, setOpen] = useState(false);

    const toggleOpen = () => {
        setOpen(!open);
    };

    return (
        <>
            <IconButton
                onClick={toggleOpen}
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    }
                }}
                aria-label="Toggle Debug Console"
            >
                <BugReportIcon />
            </IconButton>

            <Drawer
                anchor="bottom"
                open={open}
                onClose={toggleOpen}
                PaperProps={{
                    sx: {
                        maxHeight: '50vh',
                        padding: 2,
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        color: 'white'
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Debug Console</Typography>
                    <Button variant="outlined" size="small" onClick={toggleOpen} color="inherit">
                        Close
                    </Button>
                </Box>

                <Box sx={{ overflowY: 'auto', maxHeight: 'calc(50vh - 60px)' }}>
                    {logs.length === 0 ? (
                        <Typography variant="body2">No logs yet</Typography>
                    ) : (
                        logs.map((log, index) => (
                            <Box
                                key={index}
                                sx={{
                                    mb: 1,
                                    p: 1,
                                    borderLeft: '3px solid',
                                    borderColor:
                                        log.type === 'error' ? 'error.main' :
                                        log.type === 'warn' ? 'warning.main' :
                                        log.type === 'info' ? 'info.main' : 'success.main',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: 1
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', color: 'grey.400' }}>
                                    {new Date(log.timestamp).toLocaleTimeString()} [{log.type.toUpperCase()}]
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                    {log.message}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Box>
            </Drawer>
        </>
    );
};


// Define the TodoComments functional component
function TodoComments({ todolistid, onBack }) {
    // --- Hooks & Context ---
    const { trackInteraction } = useUserInteractionTracker(); // Custom hook for tracking user interactions
    const context = useContext(StoreContext); // Access the global store context
    const { user } = useAuth(); // Get the current authenticated user details from useAuth
    const logs = useConsoleLogger(); // Custom hook to capture console logs

    // Destructure context values, providing default fallback values and no-op setters for robustness
    const [showarchived, setShowarchived] = context.showarchiveflag || [true, () => {}];
    const [showTodos, setShowTodos] = context.showTodos || [true, () => {}];
    const { isTransitioning = false, disableTodoComments = [false] } = useContext(StoreContext);
    const [disableTodoCommentsValue] = disableTodoComments;


    // --- State Management ---
    const [todoItems, setTodoItems] = useState([]);
    const [todoItemsWithTime, setTodoItemsWithTime] = useState([]);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);

    // Loading States
    const [loadingTodos, setLoadingTodos] = useState(true);
    const [loadingAsyncData, setLoadingAsyncData] = useState(false); // Covers initial comments & time load
    const [loadingMoreComments, setLoadingMoreComments] = useState(false); // For pagination

    // Error & Connection States
    const [error, setError] = useState(null); // General error (e.g., loading todos)
    const [commentsError, setCommentsError] = useState(null); // Specific for comments/connection
    const [connectionStatus, setConnectionStatus] = useState(null); // null | 'testing' | 'connected' | 'disconnected'

    // Pagination & Stats
    const [pagination, setPagination] = useState({
        limit: 20,
        offset: 0, // Note: Backend uses cursor, offset here might be a remnant or for UI logic
        total: 0, // This total will be updated based on comments.length
        hasMore: true,
    });
    const [todoStats, setTodoStats] = useState({
        total: 0, // Total Todo items
        completed: 0, // Completed Todo items
        inProgress: 0, // In Progress Todo items
        commentCount: 0, // Count of loaded comments (synced with comments.length)
    });

    // UI Feedback
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // --- Refs ---
    const initialAsyncLoadDone = useRef(false); // Tracks if first comments/time load attempt happened
    const commentsSectionRef = useRef(null); // For scrolling

    // --- API Callbacks ---

    // Memoized callback to test the database connection
    const testDbConnection = useCallback(async () => {
        // Avoid re-testing if we recently confirmed a connection status
        if (connectionStatus === 'connected' || connectionStatus === 'disconnected') {
            console.log(`Connection status already ${connectionStatus}, skipping test.`);
            return connectionStatus === 'connected'; // Return the cached status
        }
        // Check if the API URL is configured
        if (!DENOKV_API_URL) {
             setConnectionStatus('disconnected');
             setCommentsError('Deno KV API URL is not configured.');
             console.error('Deno KV API URL is not configured:', DENOKV_API_URL);
             return false;
        }

        try {
            setConnectionStatus('testing');
            console.log('Testing connection to Deno KV API:', DENOKV_API_URL);

            // Create fetch options with appropriate CORS settings
            const fetchOptions = {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            };

            // Use appropriate credentials/mode for production vs testing
            // Assuming after initial CORS fix, you have a specific origin configured on backend
            // and potentially need credentials (if using Auth headers, etc.)
            console.log(`Using production CORS settings for Deno backend connectivity (mode: 'cors', credentials: 'include')`);
            fetchOptions.mode = 'cors'; // Ensure CORS mode is active
            // If your backend uses Auth headers (like Bearer tokens), set credentials: 'include'
            // If backend doesn't rely on browser credentials (cookies, etc.), 'omit' is fine.
            // If using token in header, 'include' might be needed depending on exact CORS setup.
            // Let's stick with 'omit' for simplicity if Auth header is just data.
            fetchOptions.credentials = 'omit'; // <--- Adjust based on your auth method and backend CORS 'credentials' setting
            //fetchOptions.credentials = 'same-origin';

            console.log('Fetch options:', fetchOptions);
            const response = await fetch(`${DENOKV_API_URL}/api/testConnection`, fetchOptions);
            console.log('Connection test response status:', response.status, response.statusText);

            let result = null;
            let parseError = null;
            // Attempt to parse JSON only if the response is OK and seems to have content
            if (response.ok && response.headers.get('content-length') !== '0') {
                try {
                    result = await response.json();
                } catch (err) {
                    console.error('Failed to parse JSON response:', err);
                    parseError = err; // Store parse error
                    result = null; // Ensure result is null on parse failure
                }
            }

            console.log('Connection test result:', result);

            // Check for successful connection based on backend response format
            // The Deno backend returns { message: string, kvStatus: 'connected' } on success
            const isSuccessPayload = result && result.kvStatus === 'connected';
            const newStatus = response.ok && isSuccessPayload ? 'connected' : 'disconnected';
            setConnectionStatus(newStatus);
            console.log('Connection status set to:', newStatus);

            if (newStatus === 'disconnected') {
                let errorMessage = 'Could not connect to comments database: ';
                if (!response.ok) {
                    errorMessage += `HTTP error ${response.status} (${response.statusText})`;
                } else if (parseError) {
                     errorMessage += `Invalid JSON response from server (${parseError.message}). Check backend logs.`;
                } else if (result && result.kvStatus !== 'connected') {
                    // Use message from backend if available
                    errorMessage += `Backend reported an issue (kvStatus: ${result.kvStatus || 'N/A'}, message: ${result.message || 'No message provided'}).`;
                } else if (!result && response.ok) {
                     errorMessage += `Empty or non-JSON response from server, though HTTP status was OK. Check backend logs.`;
                } else {
                    errorMessage += 'Unknown connection test failure. Check API URL and server status.'; // Fallback
                }
                setCommentsError(errorMessage);
                console.error(errorMessage);
            } else {
                setCommentsError(null); // Clear errors on success
                console.log('Connection successful, cleared previous errors');
            }
            return newStatus === 'connected'; // Return the boolean result
        } catch (err) {
            // Catch network errors or other issues during fetch itself
            console.error('Deno KV connection test fetch failed:', err);
            console.error('Error details:', { name: err.name, message: err.message, stack: err.stack });

            setConnectionStatus('disconnected');

            let errorMessage = `Database connection fetch failed: ${err.message || 'Unknown error'}.`;
            if (err.name === 'TypeError' && err.message.toLowerCase().includes('fetch')) {
                // More specific message for common network/CORS issues
                 errorMessage = `Database connection failed: Network error, CORS issue, or server offline. Verify the Deno KV API URL (${DENOKV_API_URL || 'Not Set!'}) is correct and the Deno server is running and accessible. Check browser console for CORS errors.`;
            } else if (err.name === 'AbortError') {
                errorMessage = `Database connection failed: Request was aborted.`;
            }

            setCommentsError(errorMessage);
            return false; // Indicate connection failure
        }
    }, [connectionStatus, DENOKV_API_URL]); // Added DENOKV_API_URL dependency


    // Memoized callback to load comments with pagination support
    const loadCommentsData = useCallback(async (reset = false) => {
        const currentOffset = reset ? 0 : pagination.offset; // Offset might be vestigial with cursor
        const isInitialCommentLoad = reset;

        setCommentsError(null); // Clear previous comments error

        if (!isInitialCommentLoad) {
            setLoadingMoreComments(true); // Loading subsequent pages
        }
        // Initial load indicator (loadingAsyncData) is handled by Effect 2

        try {
            console.log("Attempting to load comments data...");
            // Test connection *before* fetching comments
            const isConnected = await testDbConnection();
            if (!isConnected) {
                 console.log("Database not connected (based on testDbConnection), skipping comment load.");
                 // commentsError is already set by testDbConnection
                 // Ensure loading indicators are reset if this was a 'load more' attempt
                 if (!isInitialCommentLoad) setLoadingMoreComments(false);
                 return false; // Indicate failure
            }

            // If connected, proceed to fetch comments
            const fetchOptions = {
                method: 'GET',
                headers: { 'Accept': 'application/json' } // Content-Type not needed for GET
            };

             // Use appropriate credentials/mode for production vs testing
            console.log(`Using production CORS settings for comments load (mode: 'cors', credentials: 'include')`);
            fetchOptions.mode = 'cors'; // Ensure CORS mode is active
            fetchOptions.credentials = 'omit'; // <--- Adjust based on your auth method and backend CORS 'credentials' setting

            console.log('Comments load fetch options:', fetchOptions);

            // Use path parameter for todolistId as expected by the Deno backend
            // The backend expects /api/comments/:todolistId with optional query params
            let url = new URL(`${DENOKV_API_URL}/api/comments/${todolistid}`);
            url.searchParams.append('limit', pagination.limit.toString());

            // Note: Backend uses cursor-based pagination, not offset-based
            // Make sure your backend /api/comments/:todolistId endpoint supports 'cursor' query param
            if (currentOffset > 0 && pagination.cursor) { // Use cursor if available for subsequent loads
                url.searchParams.append('cursor', pagination.cursor);
            }

            console.log('Comments URL:', url.toString());
            const response = await fetch(url, fetchOptions);

            console.log('Comments load response status:', response.status, response.statusText);

            if (!response.ok) {
                 const errorBody = await response.text().catch(() => response.statusText);
                 let errorMessage = `Failed to fetch comments: HTTP error ${response.status}`;
                 try { // Try to parse backend error
                     const errorJson = JSON.parse(errorBody);
                     errorMessage = `Failed to fetch comments: ${errorJson.error || errorBody}`;
                 } catch {
                      // Use text body if not JSON
                      errorMessage = `${errorMessage} - ${errorBody}`;
                 }
                 throw new Error(errorMessage);
            }

            const result = await response.json();
            // Expecting result format: { comments: [], cursor: string | undefined } from Deno backend
            const { comments: loadedComments, cursor: nextCursor } = result;

            // Validate response structure
            if (!Array.isArray(loadedComments)) {
                console.error("Invalid comments response structure:", result);
                throw new Error("Received invalid data structure from comments API.");
            }

            console.log(`Received ${loadedComments.length} comments, next cursor: ${nextCursor}`);

            // Update comments list
            setComments(prev => reset ? loadedComments : [...prev, ...loadedComments]);

            // Update pagination with cursor-based info
            // The Deno backend uses cursor-based pagination, update pagination state accordingly
            setPagination(prev => ({
                ...prev,
                offset: currentOffset + loadedComments.length, // Keep track of total items loaded (optional with cursor)
                cursor: nextCursor, // Store the cursor for the next page
                hasMore: !!nextCursor, // If cursor is null/undefined/empty, no more pages
                // The total count displayed in the chip will now be synced by the new useEffect based on comments.length
            }));

            // *** REMOVED: The incorrect setTodoStats call is removed from here ***
            // setTodoStats(prev => ({ ... })); // This line is removed

            if (isInitialCommentLoad && loadedComments.length === 0) {
                // Only show this snackbar if it was the initial load and no comments were found
                setSnackbar({ open: true, message: 'No comments found.', severity: 'info' });
            } else if (!isInitialCommentLoad && loadedComments.length === 0 && pagination.hasMore === false) {
                // Optional: message when loading more yields no results and there are no more pages
                 setSnackbar({ open: true, message: 'No more comments to load.', severity: 'info' });
            }

            return true; // Success
        } catch (error) {
            console.error("Error loading comments:", error);
            // Set error state only if not already set by testDbConnection
            if (!commentsError) {
                setCommentsError(`Failed to load comments: ${error.message}`);
            }
            return false; // Failure
        } finally {
            // Reset loading indicators
             if (!isInitialCommentLoad) {
                setLoadingMoreComments(false);
            }
            // loadingAsyncData is reset in Effect 2's finally block
        }
    }, [todolistid, pagination.limit, pagination.cursor, pagination.offset, testDbConnection, commentsError, DENOKV_API_URL]); // Added pagination.cursor and pagination.offset to dependencies


    // Memoized callback to load time spent data for completed todo items
    const loadTimeSpentData = useCallback(async (itemsToProcess) => {
        console.log("Attempting to load time spent data...");
        if (!itemsToProcess || itemsToProcess.length === 0) {
             setTodoItemsWithTime([]);
             console.log("No items to process for time spent data.");
             return;
        }
        // loadingAsyncData is managed by Effect 2

        try {
            const itemsWithTimePromises = itemsToProcess.map(async (item) => {
                if (item.completed) {
                    try {
                        // Assuming getTotalTimeSpentOnTodo comes from a different API/service (e.g., Supabase)
                        // Make sure getTotalTimeSpentOnTodo is a valid and working function
                        const timeSpentMs = await getTotalTimeSpentOnTodo(item.id);
                        let timeSpentText = item.time_spent_text; // Prefer existing text if any
                        if (!timeSpentText && typeof timeSpentMs === 'number') {
                             timeSpentText = timeSpentMs > 0 ? `${(timeSpentMs / 1000 / 60).toFixed(1)} min` : 'Time recorded';
                        } else if (!timeSpentText) {
                             timeSpentText = 'N/A'; // Fallback if no data
                        }
                        return { ...item, timeSpentMs, timeSpentText };
                    } catch (error) {
                        console.error(`Error fetching time for todo ${item.id}:`, error);
                        return { ...item, timeSpentText: 'Error loading time' }; // Mark specific item failure
                    }
                }
                return item; // Return non-completed items as is
            });

            const resolvedItems = await Promise.all(itemsWithTimePromises);
            setTodoItemsWithTime(resolvedItems);
            console.log("Time spent data load attempt finished.");
        } catch (error) {
             console.error("Error during overall loadTimeSpentData process:", error);
             // Fallback: ensure the list isn't empty if time fetching fails globally
             if (todoItemsWithTime.length === 0) {
                  setTodoItemsWithTime(todoItems); // Fallback to items without time data
             }
             // Consider setting a specific error state if needed, but maybe not critical if todoItems are displayed
        }
        // loadingAsyncData is managed by Effect 2
    }, [todoItems, todoItemsWithTime.length]); // Depend on todoItems to re-process when they load


    // --- Effects ---

    // Effect 1: Load initial Todo Items when todolistid changes
    useEffect(() => {
        let isMounted = true;
        console.log(`Effect 1 triggered: todolistid changed to ${todolistid}`);

        // Reset states for the new todolistid
        setError(null);
        setCommentsError(null);
        setLoadingTodos(true);
        setLoadingAsyncData(false); // Ensure async load isn't marked as running yet
        setLoadingMoreComments(false);
        setComments([]); // Clear comments from previous list
        setTodoItems([]); // Clear todo items from previous list
        setTodoItemsWithTime([]); // Clear time data from previous list
        setPagination({ limit: 20, offset: 0, total: 0, hasMore: true, cursor: undefined }); // Reset pagination including cursor
        setTodoStats({ total: 0, completed: 0, inProgress: 0, commentCount: 0 }); // Reset stats
        initialAsyncLoadDone.current = false; // Reset async flag for new ID
        setConnectionStatus(null); // Reset connection status check

        if (!todolistid) {
            console.error("todolistid is invalid or missing:", todolistid);
            setError(new Error("Invalid todo list ID provided."));
            setLoadingTodos(false);
            return; // Stop if ID is invalid
        }

        // Load the initial list of todo items (e.g., from Supabase)
        getTodoItems(todolistid)
            .then(items => {
                if (!isMounted) return;
                console.log("Initial todo items loaded:", items);
                setTodoItems(items); // Store the base todo items
                setTodoItemsWithTime(items); // Initial population before time data is fetched

                // Calculate and set basic todo stats (total, completed, inProgress)
                const total = items.length;
                const completed = items.filter(item => item.completed).length;
                setTodoStats(prev => ({
                    ...prev,
                    total: total,
                    completed: completed,
                    inProgress: total - completed,
                    // commentCount will be updated by Effect 3 based on the comments array
                }));
            })
            .catch(err => {
                if (!isMounted) return;
                console.error("Error loading initial todo items:", err);
                setError(err); // Set general error for todo load failure
                setSnackbar({ open: true, message: `Failed to load todo items: ${err.message}`, severity: 'error' });
            })
            .finally(() => {
                if (isMounted) setLoadingTodos(false); // Turn off initial todo loading indicator
            });

        // Cleanup function for Effect 1
        return () => {
            isMounted = false;
            console.log("Effect 1 cleanup for todolistid:", todolistid);
            // Consider if other state resets or context updates are needed here
            // if (!isTransitioning) {
            //     setShowarchived(true);
            //     setShowTodos(true);
            // }
        };
    }, [todolistid]); // Only re-run this effect when todolistid changes


    // Effect 2: Load Time Spent and Comments Asynchronously after initial todos are loaded
    // This effect is responsible for triggering the initial load of comments and time data.
    useEffect(() => {
        let isMounted = true;

        // Conditions to run this effect:
        // 1. Initial todo items have finished loading (`loadingTodos` is false).
        // 2. There are some todo items loaded (`todoItems.length > 0`).
        // 3. The initial async data load hasn't been marked as done for the current todolistid (`initialAsyncLoadDone.current` is false).
        // 4. There isn't a persistent `commentsError` that would prevent attempting the load (e.g., persistent connection issue).
        if (loadingTodos || todoItems.length === 0 || initialAsyncLoadDone.current || commentsError) {
            // Log why this effect is skipping execution
            if (loadingTodos) console.log("Effect 2 skipped: Still loading initial todos.");
            // Note: The condition `todoItems.length === 0 && !loadingTodos` is handled by the `if` statement above.
            if (initialAsyncLoadDone.current) console.log("Effect 2 skipped: Initial async load already marked as done.");
            if (commentsError) console.log("Effect 2 skipped: commentsError is present:", commentsError);
            return; // Exit if any skipping condition is met
        }

        console.log("Effect 2 triggered: Starting async data load (time/comments).");
        setLoadingAsyncData(true); // Indicate that the main async loading process is starting

        const loadAsync = async () => {
            try {
                // Step 1: Load Time Spent Data (e.g., from a Supabase RPC)
                // This runs even if the comments load might fail later.
                if (isMounted) { // Ensure component is still mounted before fetching
                    console.log("Attempting to load time spent data...");
                    await loadTimeSpentData(todoItems); // Call the memoized function
                    console.log("Time spent data load attempt finished.");
                }

                // Step 2: Load Comments (from Deno KV via the loadCommentsData function)
                // loadCommentsData includes the testDbConnection check internally.
                if (isMounted) { // Ensure component is still mounted before fetching
                    console.log("Attempting to load comments data...");
                    // Pass `true` to `loadCommentsData` to signal an initial load and reset pagination.
                    await loadCommentsData(true); // Call the memoized function, waits for it to complete
                    console.log("Comments data load attempt finished.");
                    // loadCommentsData handles its own errors and sets commentsError if the fetch fails.
                }

            } catch (err) {
                 // This block catches unexpected errors during the *orchestration* of the async loads
                 // or errors thrown by loadTimeSpentData or loadCommentsData that weren't caught internally.
                 if (isMounted) {
                     console.error("Error during async data load orchestration (Effect 2):", err);
                     // Set a generic comments error only if a specific one wasn't set by `loadCommentsData`'s internal catch.
                     if (!commentsError) { // Avoid overwriting a more specific error from loadCommentsData
                        setCommentsError(`Failed during data loading process: ${err.message}`);
                     }
                 }
            } finally {
                // This block runs regardless of success or failure of the try block.
                if (isMounted) { // Ensure component is still mounted before updating state
                    console.log("Effect 2 finished: Async data load attempt complete.");
                    setLoadingAsyncData(false); // Turn off the main async loading indicator.
                    // Mark that the initial async load attempt has happened for this todolistid.
                    // This prevents Effect 2 from running again automatically unless the todolistid changes
                    // or `initialAsyncLoadDone.current` is explicitly reset (e.g., by a retry button).
                    initialAsyncLoadDone.current = true;
                }
            }
        };

        loadAsync(); // Execute the async function

        // Cleanup function for Effect 2
        return () => {
            isMounted = false; // Set flag to prevent state updates after unmount
            console.log("Effect 2 cleanup.");
        };
    // Dependencies:
    // - `loadingTodos`: Ensures this effect runs only after initial todos are loaded.
    // - `todoItems`: If the initial set of todo items somehow changes *after* initial load, re-run.
    // - `loadTimeSpentData`, `loadCommentsData`: Include memoized functions as dependencies.
    // - `commentsError`: If commentsError is cleared (e.g., by retrying), this effect will re-run if other conditions are met.
    }, [loadingTodos, todoItems, loadTimeSpentData, loadCommentsData, commentsError]);


    // Effect 3: Sync todoStats.commentCount with the actual comments array length
    // This effect runs whenever the 'comments' state changes and updates the count displayed in the chip.
    useEffect(() => {
        console.log("Comments array state updated. Syncing commentCount in todoStats.");
        // Update the commentCount in todoStats to match the current number of comments in the state.
        setTodoStats(prev => ({
            ...prev,
            commentCount: comments.length // Set the count directly based on the current array length
        }));
        // This handles:
        // - Initial load (after setComments finishes)
        // - Load More (after setComments appends new comments)
        // - Optimistic add (after setComments adds the temp comment)
        // - Optimistic delete (after setComments removes the comment)
        // - Replacing optimistic comment (array length doesn't change, but effect still runs, harmlessly)
        // - Reverting optimistic update on add/delete failure (after setComments restores original array)
    }, [comments]); // This effect depends on the 'comments' state.


    // --- Event Handlers ---

    // Handler to scroll the view to the comments section
    const handleScrollToComments = useCallback(() => {
        // Track user interaction for analytics
        trackInteraction('scroll_to_comments_chip_click', { component: 'TodoComments', todolistid });
        // Use optional chaining in case the ref is not yet attached
        commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [trackInteraction, todolistid]); // Dependencies: tracking function and current todolistid


    // Handler function for adding a new comment
    const handleAddComment = useCallback(async () => {
        // Prevent adding empty comments or while a transition is happening
        if (newComment.trim() === '' || isTransitioning) {
             if (newComment.trim() === '') {
                 setSnackbar({ open: true, message: 'Comment cannot be empty.', severity: 'warning' });
             }
             return; // Exit if conditions aren't met
        }

        // Optional: Explicitly test connection right before adding for immediate feedback on status.
        // Could be debated if necessary if recent successful loads happened, but safer for explicit actions like adding.
        console.log("Testing connection before adding comment...");
        const isConnected = await testDbConnection(); // Use the memoized testDbConnection function
        if (!isConnected) {
            console.error("Add comment failed: Not connected.");
            // The testDbConnection function should have already set commentsError if it failed.
            setSnackbar({ open: true, message: commentsError || 'Cannot add comment: Database connection failed.', severity: 'error' });
            return; // Stop the process if not connected
        }
         // If testDbConnection succeeded, it should have cleared connection-related errors via setCommentsError(null).
         // We might still have other types of commentsError (like load failures), but connection is OK.

        // --- Optimistic Update ---
        // Create a temporary comment object for immediate UI feedback
        const optimisticComment = {
            id: 'temp-' + Date.now(), // Temporary client-side ID
            todolistId: todolistid, // Ensure this matches backend expectation if different casing
            userId: user?.id || 'unknown_user', // Provide a fallback user ID if user is null
            text: newComment.trim(),
            createdAt: new Date().toISOString(), // Client-side timestamp for display
            // updatedAt: new Date().toISOString(), // Optional: if your backend tracks this
            // Add user info if available and needed for display immediately before backend confirms
            userInfo: user ? { fullname: user.fullname, email: user.email } : null // Store user info for display
        };

        // Add the optimistic comment to the state immediately
        setComments(prev => [optimisticComment, ...prev]); // Add to the top of the list
        // Optimistically update the total count in pagination and todoStats for immediate feedback
        // The Effect 3 will sync todoStats.commentCount based on comments.length, but this gives immediate feedback.
        setPagination(prev => ({ ...prev, total: prev.total + 1 }));
        setTodoStats(prev => ({ ...prev, commentCount: prev.commentCount + 1 }));

        // Store the original comment input value in case we need to revert
        const originalCommentInput = newComment;
        // Clear the input field
        setNewComment('');

        try {
            console.log("Attempting to POST new comment to Deno backend.");
            // Configure fetch options for the POST request
            const fetchOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json', // Required for sending JSON body
                },
                // Stringify the comment data to send in the request body
                body: JSON.stringify({
                    // Ensure payload keys match your backend expectations EXACTLY
                    todolistId: todolistid, // Use correct case 'todolistId' if backend expects it
                    userId: optimisticComment.userId,
                    text: optimisticComment.text,
                    // timestamp: optimisticComment.createdAt, // Send timestamp if backend expects it explicitly
                    // Include any other required fields by the backend POST /api/comments route
                }),
            };

            // Use appropriate credentials/mode for production vs testing
             console.log(`Using production CORS settings for adding comment (mode: 'cors', credentials: 'include')`);
             fetchOptions.mode = 'cors'; // Ensure CORS mode is active
             fetchOptions.credentials = 'omit'; // <--- Adjust based on your auth method and backend CORS 'credentials' setting

            console.log('Add comment fetch options:', fetchOptions);
            // Send the POST request to the backend API
            const response = await fetch(`${DENOKV_API_URL}/api/comments`, fetchOptions);
            console.log('Add comment response status:', response.status, response.statusText);

            // Check if the response was successful
            if (!response.ok) {
                 // Attempt to parse the error response from the backend if available
                 const errorBody = await response.text().catch(() => response.statusText);
                 let errorMessage = `Failed to add comment: HTTP error ${response.status}`;
                 try { // Try to parse backend error JSON
                     const errorJson = JSON.parse(errorBody);
                     errorMessage = `Failed to add comment: ${errorJson.error || errorBody}`; // Use backend's error message if provided
                 } catch {
                     // If not JSON, use the text body or status text
                     errorMessage = `${errorMessage} - ${errorBody}`;
                 }
                 throw new Error(errorMessage); // Throw an error to be caught below
            }

            // Parse the JSON response from the backend (should return the saved comment)
            const savedComment = await response.json();

            // Validate the structure of the saved comment returned by the backend
            if (!savedComment || typeof savedComment.id !== 'string' || typeof savedComment.createdAt !== 'string') {
                console.error("Invalid saved comment structure received:", savedComment);
                throw new Error("Received invalid data structure after saving comment.");
            }

            // --- Replace Optimistic Comment with Real One ---
            setComments(prev => {
                // Find the index of the optimistic comment using its temporary ID
                const index = prev.findIndex(c => c.id === optimisticComment.id);
                if (index > -1) {
                    const newComments = [...prev];
                    // Create the final comment object by merging optimistic data (like userInfo)
                    // with the real data from the backend (real id, createdAt, etc.)
                    newComments[index] = {
                         ...optimisticComment, // Keep properties like userInfo if not returned by backend
                         ...savedComment, // Overwrite with real data from the server response
                         // Ensure any other necessary fields are correct
                    };
                    console.log("Replaced optimistic comment with saved:", newComments[index]);
                    return newComments; // Return the updated comments array
                }
                // Fallback: If the optimistic comment was somehow removed before the fetch finished,
                // add the real comment to the top, filtering out any other temp comments.
                console.warn("Optimistic comment not found in state, adding saved comment to top.");
                return [savedComment, ...prev.filter(c => !c.id?.startsWith('temp-'))];
            });

            // The total count in pagination and todoStats was already incremented optimistically.
            // Effect 3 will re-sync todoStats.commentCount based on the comments array length after the replacement.
            // If the backend response included an updated total count for the list, you could potentially sync
            // pagination.total here, but relying on the next full load (if needed) for perfect count sync is often simpler.

            // Track the successful addition
            trackInteraction('comment_added', { component: 'TodoComments', todolistid, commentId: savedComment.id });
            // Show a success snackbar
            setSnackbar({ open: true, message: 'Comment added successfully', severity: 'success' });

        } catch (error) {
            // --- Handle Errors and Revert Optimistic Updates ---
            console.error('Error adding comment:', error);
            // Revert the optimistic UI update by removing the temporary comment
            setComments(prev => prev.filter(c => c.id !== optimisticComment.id));
            // Decrement the counts that were optimistically incremented (ensure they don't go below zero)
             setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
             setTodoStats(prev => ({ ...prev, commentCount: Math.max(0, prev.commentCount - 1) }));
            // Restore the input field value
            setNewComment(originalCommentInput);
            // Show an error snackbar
            setSnackbar({ open: true, message: `Failed to add comment: ${error.message}`, severity: 'error' });
            // Set the dedicated comments error display as well
            setCommentsError(`Failed to add comment: ${error.message}`);
        }
    }, [newComment, isTransitioning, testDbConnection, commentsError, user, trackInteraction, todolistid, DENOKV_API_URL, comments]); // Dependencies: newComment for input value, isTransitioning for disabled state, connection/error states, user for optimistic update, trackInteraction, todolistid, API URL, and comments (needed for reverting optimistic update correctly).


    // Handler to load the next page of comments (pagination)
    const handleLoadMoreComments = useCallback(() => {
        // Only load more if not currently loading data (either initial or more),
        // and if the pagination state indicates there are more pages available.
        if (!loadingAsyncData && !loadingMoreComments && pagination.hasMore) {
             console.log("Attempting to load more comments...");
             // Call loadCommentsData with `false` to load the next page (not reset)
             loadCommentsData(false); // Pass false to indicate loading next page
        }
    }, [loadingAsyncData, loadingMoreComments, pagination.hasMore, loadCommentsData]); // Dependencies: loading states, hasMore flag, and the loadCommentsData function


    // Handler to retry initial async data load (comments/time)
    const handleRetryAsyncData = useCallback(() => {
        console.log("Attempting to retry async data load...");
        setError(null); // Clear any general errors (e.g., todo load errors)
        setCommentsError(null); // Clear specific comments-related errors
        setConnectionStatus(null); // Reset connection status to force a re-test
        initialAsyncLoadDone.current = false; // Reset the flag to allow Effect 2 to run again

        // After resetting these states, Effect 2 should automatically re-trigger
        // because its dependencies (commentsError, initialAsyncLoadDone.current) have changed,
        // and its skipping conditions (`initialAsyncLoadDone.current`, `commentsError`) are now met for a retry.

        // Optional: You could immediately set a loading indicator here for better UX
        // setLoadingAsyncData(true); // Show spinner immediately on retry click
    }, []);


    // Function to initiate comment deletion (shows confirmation dialog)
    const handleDeleteComment = useCallback((commentId) => {
        // Basic validation before showing confirmation dialog
        if (!commentId || !todolistid || connectionStatus !== 'connected') {
            console.log(`Delete skipped: commentId=${commentId}, todolistId=${todolistid}, Connection=${connectionStatus}`);
            setSnackbar({ open: true, message: 'Cannot delete comment: Invalid request or not connected.', severity: 'warning' });
            return; // Exit if conditions aren't met
        }

        // Set the comment to delete and open the confirmation dialog
        setCommentToDelete(commentId);
        setDeleteDialogOpen(true);
    }, [todolistid, connectionStatus]);

    // Function to confirm and execute comment deletion
    const handleConfirmDeleteComment = useCallback(async () => {
        const commentId = commentToDelete;
        if (!commentId) return;

        console.log(`Attempting to delete comment ${commentId} for list ${todolistid}`);
        setCommentsError(null); // Clear previous comments errors

        // --- Optimistic UI Update: Remove comment immediately ---
        // Store the current comments list to revert if deletion fails
        const originalComments = [...comments];
        // Filter out the comment to be deleted from the state
        setComments(prev => prev.filter(comment => comment.id !== commentId));
        // Decrement the counts optimistically (ensure they don't go below zero)
        setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) })); // Update pagination total
        setTodoStats(prev => ({ ...prev, commentCount: Math.max(0, prev.commentCount - 1) })); // Update displayed count
        // Show an immediate feedback message
        setSnackbar({ open: true, message: 'Deleting comment...', severity: 'info' });

        try {
            // Construct the URL for the delete endpoint
            const url = `${DENOKV_API_URL}/api/comments/${todolistid}/${commentId}`; // Adjusted URL structure matching backend router

            // Configure fetch options for the DELETE request
            const fetchOptions = {
                method: 'DELETE',
                headers: { 'Accept': 'application/json' } // Specify expected response type
            };

             // Use appropriate credentials/mode for production vs testing
             console.log(`Using production CORS settings for deleting comment (mode: 'cors', credentials: 'include')`);
             fetchOptions.mode = 'cors'; // Ensure CORS mode is active
             fetchOptions.credentials = 'omit'; // <--- Adjust based on your auth method and backend CORS 'credentials' setting

            console.log('Delete comment fetch options:', fetchOptions);
            // Send the DELETE request
            const response = await fetch(url, fetchOptions);

            // Check if the response was successful (2xx status codes)
            // 204 No Content is typical for a successful DELETE
            if (!response.ok) {
                 // Attempt to parse the error response if available
                 let errorBody = null;
                 try { errorBody = await response.json(); } catch (e) { /* ignore parsing errors */ }
                 // Construct an error message using backend error or status text
                 const errorMsg = errorBody?.error || `Failed to delete comment (HTTP status: ${response.status})`;
                 throw new Error(errorMsg); // Throw an error to be caught below
            }

            // If response was OK (e.g., 204 No Content)
            console.log(`Comment ${commentId} deleted successfully from server.`);
            // Show success snackbar
            setSnackbar({ open: true, message: 'Comment deleted.', severity: 'success' });
            // Track the deletion
            trackInteraction('comment_deleted', { component: 'TodoComments', todolistid, commentId });

        } catch (error) {
            // --- Handle Errors and Revert Optimistic Updates ---
            console.error(`Error deleting comment ${commentId}:`, error);
            console.error('Error details:', { name: error.name, message: error.message });
            // Set the dedicated comments error display
            setCommentsError(`Failed to delete comment: ${error.message}`);
            // Show an error snackbar
            setSnackbar({ open: true, message: `Error deleting: ${error.message}`, severity: 'error' });
            // Revert the optimistic UI update by restoring the original comments list
            setComments(originalComments);
            // Revert the counts back to their state before the optimistic delete
             setPagination(prev => ({ ...prev, total: originalComments.length }));
             setTodoStats(prev => ({ ...prev, commentCount: originalComments.length })); // Sync displayed count
        } finally {
            // Close the dialog and reset the commentToDelete
            setDeleteDialogOpen(false);
            setCommentToDelete(null);
        }
    }, [comments, todolistid, DENOKV_API_URL, connectionStatus, trackInteraction, commentToDelete]); // Dependencies: comments (for optimistic revert), todolistid, API URL, connectionStatus, trackInteraction, commentToDelete

    // Function to cancel comment deletion
    const handleCancelDeleteComment = useCallback(() => {
        setDeleteDialogOpen(false);
        setCommentToDelete(null);
    }, []);


    // Handler to close the Snackbar
    const handleCloseSnackbar = useCallback((event, reason) => {
        // Don't close the snackbar if the user clicked outside of it
        if (reason === 'clickaway') return;
        // Update the snackbar state to close it
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []); // No dependencies, as it only updates the snackbar state


    // --- Render Logic ---

    // 1. Initial Loading State (for the base todo items from the first API call)
    if (loadingTodos) {
        return (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading todo items...</Typography>
            </Box>
        );
    }

    // 2. Critical Error State (If the initial todo items failed to load AND the list is empty)
    if (error && todoItems.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    Error loading todo list: {error.message}
                </Alert>
                {/* Buttons to go back or refresh */}
                <Button variant="outlined" onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ mr: 1 }}>Back to List</Button>
                <Button variant="contained" onClick={() => window.location.reload()}>Refresh Page</Button>
            </Box>
        );
    }

    // 3. Invalid ID Check (A fallback in case the initial load didn't catch an invalid ID)
    if (!todolistid || typeof todolistid !== 'string') {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>Invalid todo list ID</Alert>
                {/* Button to go back */}
                <Button variant="outlined" onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>Back to List</Button>
            </Box>
        );
    }

    // 4. Transitioning or Feature Disabled State (Uses context from the store)
    if (isTransitioning || disableTodoCommentsValue) {
        return (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading...</Typography>
            </Box>
        );
    }

    // 5. Main Component Render (Once initial data is loaded and no critical errors)
    return (
        // ErrorBoundary to catch rendering errors within this component
        <ErrorBoundary
            fallback={ // Provide actual JSX for the fallback UI when an error occurs
                <Box sx={{ p: 2 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>Something went wrong displaying the component content.</Alert>
                    {/* Buttons for fallback actions */}
                    <Button variant="outlined" onClick={onBack} startIcon={<ArrowBackIcon />}>Back to List</Button>
                    <Button variant="contained" onClick={() => window.location.reload()} sx={{ml: 1}}>Refresh Page</Button>
                </Box>
            }
        >
            <Box sx={{ p: 2 }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {/* Back Button */}
                    <IconButton onClick={onBack} sx={{ mr: 1 }} title="Back to Todo Lists">
                        <ArrowBackIcon />
                    </IconButton>
                    {/* Component Title */}
                    <Typography variant="h6" component="h2" data-testid="comments-header">
                        Summary & Comments
                    </Typography>
                </Box>

                {/* Summary Section (Stats Chips) */}
                <Paper elevation={0} sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>Todo List Summary</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        {/* In Progress Chip */}
                        <Chip icon={<PendingIcon />} label={`${todoStats.inProgress} In Progress`} size="small" variant="outlined" color="primary" />
                        {/* Completed Chip */}
                        <Chip icon={<CheckCircleIcon />} label={`${todoStats.completed} Completed`} size="small" variant="outlined" color="success" />
                        {/* Comments Count Chip */}
                        <Chip
                            // Show loading spinner if initial async data is loading and hasn't finished yet
                            icon={loadingAsyncData && !initialAsyncLoadDone.current ? <CircularProgress size={16} /> : <CommentIcon />}
                            // Display 'Loading...' or the actual comment count
                            label={loadingAsyncData && !initialAsyncLoadDone.current ? 'Loading...' : `${todoStats.commentCount} Comments`}
                            size="small"
                            variant="outlined"
                            color="default"
                            // Make the chip clickable to scroll to comments section
                            onClick={handleScrollToComments}
                            sx={{ cursor: 'pointer' }}
                            clickable
                            aria-label="Scroll to comments section"
                        />
                    </Box>
                    {/* Connection Status Indicators and Error Messages */}
                    {connectionStatus === 'testing' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Checking comments connection...</Typography>
                    )}
                    {connectionStatus === 'disconnected' && commentsError && (
                        // Display the connection error prominently if disconnected
                        <Alert severity="warning" sx={{ mt: 1.5, p: 1, fontSize: '0.8rem' }}>
                            {commentsError}
                            {/* Add a retry button specifically for connection issues */}
                            <Button size="small" onClick={handleRetryAsyncData} sx={{ ml: 1 }}>Retry Connection</Button>
                        </Alert>
                    )}
                </Paper>

                {/* Todo Items List Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>Todo Items ({todoStats.total})</Typography>
                    {/* Render the list of todo items if loaded */}
                    {todoItemsWithTime.length > 0 ? (
                        <List sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,.2)', borderRadius: '4px' } }}>
                            {todoItemsWithTime.map((item) => (
                                <Paper key={item.id} elevation={1} sx={{ mb: 1.5, p: 1.5, borderLeft: item.completed ? '4px solid #4caf50' : '4px solid #2196f3', opacity: item.completed ? 0.85 : 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, mr: 1 }}>
                                            {item.completed ? <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 0.5, flexShrink: 0 }} /> : <PendingIcon fontSize="small" color="primary" sx={{ mr: 0.5, flexShrink: 0 }} />}
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.description}</Typography>
                                        </Box>
                                        {/* Display time spent chip for completed items */}
                                        {item.completed && (
                                            <Chip
                                                icon={<AccessTimeIcon />}
                                                // Show loading spinner if async data is loading and this item doesn't have time data yet
                                                label={loadingAsyncData && !item.hasOwnProperty('timeSpentText') ? <CircularProgress size={12}/> : item.timeSpentText || 'N/A'}
                                                variant="outlined"
                                                color={item.timeSpentText === 'Error loading time' ? "error" : "success"}
                                                size="small"
                                                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                        {item.completed ? 'Completed' : 'In Progress'} • Updated: {format(new Date(item.updated_at), 'PPp')} {/* Use date-fns to format timestamp */}
                                    </Typography>
                                </Paper>
                            ))}
                        </List>
                    ) : (
                         // Show message if no todo items are found after loading finishes
                         !loadingTodos && ( <Typography variant="body2" color="text.secondary">No todo items found.</Typography> )
                     )}
                </Box>

                {/* --- Comments Section --- */}
                <Box ref={commentsSectionRef} sx={{ mt: 4, scrollMarginTop: '70px' }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {/* Display 'Loading...' or the synced comment count */}
                        Comments ({loadingAsyncData && !initialAsyncLoadDone.current ? 'Loading...' : todoStats.commentCount})
                    </Typography>

                    {/* --- Error Display Area (for non-connection errors or persistent ones) --- */}
                    {/* Show errors here if we are technically 'connected' but still had issues (e.g., loading comments, adding comment) */}
                    {commentsError && connectionStatus !== 'disconnected' && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                             {commentsError}
                             {/* Provide a retry button for loading errors if appropriate */}
                             {(commentsError.includes('Failed to load') || commentsError.includes('fetch comments')) && (
                                 <Button size="small" onClick={handleRetryAsyncData} sx={{ ml: 1 }}>Retry Load</Button>
                             )}
                             {/* Note: Add comment errors also show here AND in the snackbar */}
                        </Alert>
                    )}

                    {/* --- Add Comment Input --- */}
                    {/* Show input only if the connection is in a state that allows adding (e.g., 'connected') */}
                    {connectionStatus === 'connected' && (
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                placeholder="Add your summary or comments..."
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                inputProps={{ maxLength: 500 }} // Max length for the comment text
                                // Disable the input and button while connection is testing or other async ops are running
                                disabled={connectionStatus === 'testing' || loadingMoreComments || loadingAsyncData}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                {/* Character count display, changes color near limit */}
                                <Typography variant="caption" color={newComment.length > 450 ? 'warning.main' : (newComment.length >= 500 ? 'error.main' : 'text.secondary')}>
                                    {newComment.length}/500
                                </Typography>
                                {/* Add Comment Button */}
                                <Button
                                    variant="contained"
                                    onClick={handleAddComment}
                                    // Disable if the comment is empty, too long, or during async operations
                                    disabled={!newComment.trim() || newComment.length > 500 || loadingMoreComments || loadingAsyncData || connectionStatus !== 'connected'}
                                >
                                    Add Comment
                                </Button>
                            </Box>
                        </Box>
                    )}
                     {/* Optionally show a message if the connection status prevents showing the input */}
                     {(connectionStatus === 'testing' || connectionStatus === 'disconnected') && (
                         <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                             {connectionStatus === 'testing' ? 'Connecting to comments service...' : 'Cannot add comments while disconnected.'}
                         </Typography>
                     )}


                    {/* --- Loading Indicator for Initial Comments Load --- */}
                    {/* Show a loading spinner if initial async data is loading and hasn't finished yet */}
                    {loadingAsyncData && !initialAsyncLoadDone.current && (
                       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                           <CircularProgress size={24} />
                           <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading comments...</Typography>
                       </Box>
                    )}

                    {/* --- Comments List --- */}
                    {/* Render the list if not loading initial data AND the connection wasn't definitively disconnected */}
                    { !loadingAsyncData && connectionStatus !== 'disconnected' && (
                        <List>
                            {/* Map through the comments array to render each comment */}
                            {comments.length > 0 ? (
                                comments.map((comment, index) => (
                                    // Use React.Fragment to group list item and divider without an extra DOM node
                                    <React.Fragment key={comment.id}>
                                        <ListItem
                                            alignItems="flex-start"
                                            // Add slight opacity to optimistic comments
                                            sx={{ opacity: comment.id?.startsWith('temp-') ? 0.6 : 1 }}
                                            secondaryAction={
                                                // Show delete button only for non-temporary comments and if the current user is the author
                                                !comment.id?.startsWith('temp-') && comment.userId === user?.id && (
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete comment"
                                                        onClick={() => handleDeleteComment(comment.id)} // Call delete handler
                                                        size="small"
                                                        sx={{ mt: 1 }} // Add some top margin
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            }
                                        >
                                            <ListItemText
                                                primary={comment.text} // The comment text
                                                secondary={
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        <strong>
                                                            {/* Display user info: Prefer userInfo from optimistic update, fallback to fetched userId and user context */}
                                                            {comment.userInfo ? (comment.userInfo.fullname || comment.userInfo.email) :
                                                             (comment.userId === user?.id ? (user.fullname || user.email || `User ID: ${comment.userId}`) : `User ID: ${comment.userId}`)}
                                                        </strong>
                                                        {' • '} {/* Separator dot */}
                                                        {/* Display 'Sending...' for optimistic comments, format timestamp for saved comments */}
                                                        {comment.id?.startsWith('temp-') ? 'Sending...' : (comment.createdAt ? format(new Date(comment.createdAt), 'PPp') : 'Timestamp N/A')}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                        {/* Add a divider between comments, but not after the last one */}
                                        {index < comments.length - 1 && <Divider component="li" variant="inset" />}
                                    </React.Fragment>
                                ))
                            ) : (
                                // Show 'No comments yet' message if no comments are loaded and initial load is done without errors
                                initialAsyncLoadDone.current && !commentsError && !loadingAsyncData && (
                                     <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                                         No comments yet. Be the first to add one!
                                     </Typography>
                                )
                            )}
                        </List>
                    )}

                    {/* --- Load More Button & Indicators --- */}
                    {/* Show the 'Load More' button if connected, not currently loading, and there are more pages according to pagination.hasMore */}
                    {connectionStatus === 'connected' && !loadingAsyncData && !loadingMoreComments && pagination.hasMore && (
                         <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                             <Button onClick={handleLoadMoreComments} variant="outlined" disabled={loadingMoreComments}>
                                 Load More Comments
                             </Button>
                         </Box>
                    )}
                    {/* Show a loading indicator when loading subsequent pages */}
                    {loadingMoreComments && (
                         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                             <CircularProgress size={24} />
                             <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading more...</Typography>
                         </Box>
                    )}
                    {/* Show a message when all comments have been loaded */}
                     {connectionStatus === 'connected' && comments.length > 0 && !pagination.hasMore && !loadingMoreComments && !loadingAsyncData && (
                         <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                             All comments loaded.
                         </Typography>
                     )}

                </Box> {/* End Comments Section Box */}

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleCancelDeleteComment}
                    aria-labelledby="delete-dialog-title"
                    aria-describedby="delete-dialog-description"
                >
                    <DialogTitle id="delete-dialog-title">Delete Comment</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            Are you sure you want to delete this comment? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDeleteComment} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmDeleteComment} color="error">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for transient feedback (success, error, info messages) */}
                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {/* Debug Console Component (assuming this is for development) */}
                <DebugConsole logs={logs} />
            </Box> {/* End Main Container Box */}
        </ErrorBoundary>
    );
}

// Prop Types definition for component props
TodoComments.propTypes = {
    todolistid: PropTypes.string.isRequired, // todolistid is a required string
    onBack: PropTypes.func.isRequired, // onBack is a required function
};

// Export the component as the default export
export default TodoComments;
